import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ExternalLink, FileText, Flag, Link2, Paperclip, X } from "lucide-react";
import { SprintTaskTimeline } from "./SprintTimelineTab";
import SprintReportLink from "./SprintReportLink";
import { sprintService, apiErrorMessage } from "../../api/taskBoardService";
import { STATUS_LABELS, TASK_STATUSES } from "./constants";
import { EmptyState, ErrorState, StatusPill, daysUntil, formatDate, SkeletonRows } from "./shared";

const timeRemaining = (sprint) => {
  if (sprint.status === "completed") return "Completed";
  const toStart = daysUntil(sprint.startDate);
  if (toStart > 0) return `Starts in ${toStart} day${toStart === 1 ? "" : "s"}`;
  const left = daysUntil(sprint.endDate);
  if (left < 0) return `Ended ${-left} day${left === -1 ? "" : "s"} ago`;
  if (left === 0) return "Ends today";
  return `${left} day${left === 1 ? "" : "s"} left`;
};

const Block = ({ title, children }) => (
  <div className="space-y-1.5">
    <h3 className="text-[11px] font-semibold uppercase tracking-[0.08em] text-faint">{title}</h3>
    {children}
  </div>
);

const Prose = ({ text, empty }) =>
  text ? (
    <p className="text-sm text-gray-800 dark:text-light-200 whitespace-pre-wrap break-words">{text}</p>
  ) : (
    <p className="text-sm italic text-gray-400 dark:text-light-500">{empty}</p>
  );

/**
 * Right-hand sprint details panel. The selected sprint is the board's sprint
 * filter, so panel and board always describe the same sprint. Everything is
 * loaded from the API; `refreshKey` changes when relevant tasks or the sprint
 * itself change (realtime), which reloads the summary.
 */
const SprintPanel = ({
  sprintId,
  sprints,
  refreshKey,
  onSelectSprint,
  onClose,
  statuses = TASK_STATUSES.filter((s) => s.value !== "product_backlog"),
  // Board filters that narrow the counts (admins / squad leads).
  squad = "",
  assignee = "",
  // Admin flag (document actions).
  canEditMilestones = false,
}) => {
  const [sprint, setSprint] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const load = useCallback(
    async (silent) => {
      if (!sprintId) return;
      if (!silent) setLoading(true);
      setError("");
      try {
        // Counts mirror the user's board: only tasks they can see, narrowed by
        // the board's squad / assignee filters. The sprint details page shows
        // totals for the whole sprint instead.
        setSprint((await sprintService.get(sprintId, { scope: "visible", squad, assignee })).data);
      } catch (err) {
        setError(apiErrorMessage(err, "Could not load the sprint"));
      } finally {
        setLoading(false);
      }
    },
    [sprintId, squad, assignee],
  );

  // A different sprint starts empty; a filter change keeps the current view
  // while the new counts load.
  useEffect(() => {
    setSprint(null);
  }, [sprintId]);

  useEffect(() => {
    load(false);
  }, [load]);

  useEffect(() => {
    if (refreshKey) load(true);
  }, [refreshKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const done = sprint?.taskCounts?.done || 0;
  const total = sprint?.totalTasks || 0;
  const pct = total ? Math.round((done / total) * 100) : 0;

  return (
    <div className="flex flex-col h-full bg-white dark:bg-primary-card">
      <header className="flex items-center justify-between gap-2 px-4 py-3 border-b border-gray-200 dark:border-dark-100">
        <div className="flex items-center gap-2 min-w-0">
          <Flag className="w-4 h-4 text-primary-accent shrink-0" />
          <h2 className="text-sm font-semibold text-gray-900 dark:text-light-100">Sprint details</h2>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="p-1 rounded-md text-gray-500 hover:text-primary-accent hover:bg-primary-accent/10"
          aria-label="Close sprint panel"
        >
          <X className="w-4 h-4" />
        </button>
      </header>

      <div className="px-4 pt-3">
        <select
          value={sprintId || ""}
          onChange={(e) => onSelectSprint(e.target.value)}
          className="input-field !py-1.5 text-sm w-full"
          aria-label="Selected sprint"
        >
          <option value="">Select a sprint…</option>
          {sprints.map((s) => (
            <option key={s._id} value={s._id}>{`${s.name} (${s.status})`}</option>
          ))}
        </select>
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-5">
        {!sprintId ? (
          <EmptyState
            icon={Flag}
            title="No sprint selected"
            message="Pick a sprint to see its goal, timeline and progress. The board is filtered to the same sprint."
          />
        ) : loading && !sprint ? (
          <SkeletonRows rows={4} />
        ) : error ? (
          <ErrorState message={error} onRetry={() => load(false)} />
        ) : sprint ? (
          <>
            <div>
              <div className="flex items-start justify-between gap-2">
                <h3 className="font-display text-[20px] font-semibold text-ink break-words">{sprint.name}</h3>
                <StatusPill status={sprint.status} />
              </div>
              <p className="text-sm text-gray-500 dark:text-light-500 mt-1">
                {formatDate(sprint.startDate)} → {formatDate(sprint.endDate)}
              </p>
              <p className="text-sm font-medium text-accent-ink mt-1">{timeRemaining(sprint)}</p>
            </div>

            <Block title="Progress">
              <div className="flex items-end justify-between text-sm text-muted">
                <span>
                  {done} of {total} task{total === 1 ? "" : "s"} done
                </span>
                <span className="font-display text-[26px] leading-none font-semibold text-ink">{pct}%</span>
              </div>
              <div className="h-2 rounded-full bg-raise overflow-hidden" role="progressbar" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100}>
                <div className="h-full rounded-full bg-grad shadow-glow transition-[width] duration-700 ease-out" style={{ width: `${pct}%` }} />
              </div>
              <ul className="grid grid-cols-2 gap-x-3 gap-y-1 mt-2 text-xs">
                {statuses.map((s) => (
                  <li key={s.value} className="flex items-center justify-between gap-2 text-gray-600 dark:text-light-400">
                    <span className="flex items-center gap-1.5 min-w-0">
                      <span className={`w-2 h-2 rounded-full shrink-0 ${s.accent}`} />
                      <span className="truncate">{STATUS_LABELS[s.value]}</span>
                    </span>
                    <span className="font-medium">{sprint.taskCounts?.[s.value] || 0}</span>
                  </li>
                ))}
              </ul>
              <p className="text-[11px] text-gray-400 dark:text-light-500">Counts include only tasks you can see.</p>
            </Block>

            <Block title="Goal">
              <Prose text={sprint.goal} empty="No goal set" />
            </Block>

            <Block title="Timeline & milestones">
              <SprintTaskTimeline sprintId={sprint._id} refreshKey={refreshKey} squad={squad} assignee={assignee} />
            </Block>

            <Block title="Important notes">
              <Prose text={sprint.notes} empty="No notes" />
            </Block>

            <Block title="Documents">
              {/* canEditMilestones is the admin flag here. */}
              <SprintReportLink sprint={sprint} admin={canEditMilestones} compact />
              {sprint.attachments?.length ? (
                <ul className="space-y-1.5">
                  {sprint.attachments.map((a) => (
                    <li key={a._id}>
                      <a
                        href={a.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 text-sm text-primary-accent hover:underline min-w-0"
                      >
                        {a.size == null ? <Link2 className="w-3.5 h-3.5 shrink-0" /> : <Paperclip className="w-3.5 h-3.5 shrink-0" />}
                        <span className="truncate">{a.name}</span>
                      </a>
                    </li>
                  ))}
                </ul>
              ) : (
                !(canEditMilestones && sprint.status === "completed") && <Prose empty="No documents attached" />
              )}
            </Block>

            <Link
              to={`/task-board/sprints/${sprint._id}`}
              className="flex items-center justify-center gap-2 w-full text-sm font-medium px-3 py-2 rounded-lg border border-primary-accent/40 text-primary-accent hover:bg-primary-accent hover:text-white transition-colors"
            >
              <FileText className="w-4 h-4" /> Open full sprint details <ExternalLink className="w-3.5 h-3.5" />
            </Link>
          </>
        ) : null}
      </div>
    </div>
  );
};

export default SprintPanel;
